import { Fragment, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type {
  Task,
  TaskGroupBy,
  TaskHubTab,
  TaskPriority,
  TaskSortField,
  TaskStatus,
} from "@sales-ops-amplify/shared";
import { emptyTaskFacets, isLinkedInTaskType } from "@sales-ops-amplify/shared";
import { cn } from "@/cn";
import { CreateTaskModal } from "@/components/tasks/create-task-modal";
import { TaskWorkbench } from "@/components/tasks/task-workbench";
import {
  useBulkTasks,
  useCompleteTask,
  useTasks,
} from "@/hooks/useTasks";
import { useDebouncedValue } from "@/hooks/usePagination";

function dueLabel(iso: string): { text: string; overdue: boolean } {
  const due = new Date(iso);
  if (Number.isNaN(due.getTime())) return { text: "No date", overdue: false };
  const now = new Date();
  const sod = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const eod = new Date(sod);
  eod.setHours(23, 59, 59, 999);
  if (due < sod) {
    const days = Math.floor((sod.getTime() - due.getTime()) / 86_400_000);
    return {
      text: days <= 0 ? "Past due" : `${days}d past due`,
      overdue: true,
    };
  }
  if (due <= eod) return { text: "Due today", overdue: false };
  return {
    text: due.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
    overdue: false,
  };
}

function typeBadge(task: Task): string {
  if (task.type === "call") return "C";
  if (task.type === "manual_email") return "E";
  if (isLinkedInTaskType(task.type)) return "LI";
  return "A";
}

function groupKey(task: Task, groupBy: TaskGroupBy): string {
  if (groupBy === "priority") return task.priority;
  if (groupBy === "status") return task.status;
  if (groupBy === "type") return task.type;
  if (groupBy === "due") {
    const d = dueLabel(task.dueAt);
    if (d.overdue) return "Overdue";
    if (d.text === "Due today") return "Today";
    return "Upcoming";
  }
  return "All";
}

const TABS: { id: TaskHubTab; label: string; facet?: keyof ReturnType<typeof emptyTaskFacets> }[] = [
  { id: "all", label: "All", facet: "all" },
  { id: "calls", label: "Calls", facet: "calls" },
  { id: "emails", label: "Emails", facet: "emails" },
  { id: "linkedin", label: "LinkedIn", facet: "linkedin" },
  { id: "action_items", label: "Action items", facet: "action_items" },
  { id: "recommended", label: "Recommended", facet: "recommended" },
];

export function TasksPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (searchParams.get("tab") as TaskHubTab | null) ?? "all";
  const [tab, setTab] = useState<TaskHubTab>(
    TABS.some((t) => t.id === initialTab) ? initialTab : "all",
  );
  const [status, setStatus] = useState<TaskStatus | "open">("open");
  const [priority, setPriority] = useState<"" | TaskPriority>("");
  const [q, setQ] = useState("");
  const debouncedQ = useDebouncedValue(q.trim(), 300);
  const [sort, setSort] = useState<TaskSortField>("due");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [groupBy, setGroupBy] = useState<TaskGroupBy>("none");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selected, setSelected] = useState<Task | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [bulkDue, setBulkDue] = useState("");

  useEffect(() => {
    const t = searchParams.get("tab") as TaskHubTab | null;
    if (t && TABS.some((x) => x.id === t) && t !== tab) setTab(t);
  }, [searchParams, tab]);

  function selectTab(next: TaskHubTab) {
    setTab(next);
    const params = new URLSearchParams(searchParams);
    if (next === "all") params.delete("tab");
    else params.set("tab", next);
    setSearchParams(params, { replace: true });
  }

  const listQuery = useTasks({
    tab,
    status,
    priority: priority || undefined,
    q: debouncedQ || undefined,
    sort,
    sortDir,
    limit: 300,
  });

  const completeMut = useCompleteTask();
  const bulkMut = useBulkTasks();

  const tasks = listQuery.data?.tasks ?? [];
  const facets = listQuery.data?.facets ?? emptyTaskFacets();

  // Keep selection / workbench in sync with the filtered list after mutations.
  useEffect(() => {
    const ids = new Set(tasks.map((t) => t.taskId));
    setSelectedIds((prev) => {
      const next = prev.filter((id) => ids.has(id));
      return next.length === prev.length ? prev : next;
    });
    setSelected((prev) => {
      if (!prev) return prev;
      const fresh = tasks.find((t) => t.taskId === prev.taskId);
      return fresh ?? null;
    });
  }, [tasks]);

  const grouped = useMemo(() => {
    if (groupBy === "none") return [{ key: "All", items: tasks }];
    const map = new Map<string, Task[]>();
    for (const t of tasks) {
      const key = groupKey(t, groupBy);
      const arr = map.get(key) ?? [];
      arr.push(t);
      map.set(key, arr);
    }
    return [...map.entries()].map(([key, items]) => ({ key, items }));
  }, [tasks, groupBy]);

  function toggleSelect(id: string) {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }

  function toggleAll() {
    if (selectedIds.length === tasks.length) setSelectedIds([]);
    else setSelectedIds(tasks.map((t) => t.taskId));
  }

  function runBulk(
    body: Parameters<typeof bulkMut.mutate>[0],
  ) {
    bulkMut.mutate(body, {
      onSuccess: () => setSelectedIds([]),
    });
  }

  return (
    <div className="space-y-4 pb-16">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Tasks</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Prioritize and complete calls, emails, LinkedIn, and action items.
          </p>
        </div>
        <button
          type="button"
          className="rounded-md bg-brand px-3 py-2 text-sm text-white"
          onClick={() => setCreateOpen(true)}
        >
          Create task
        </button>
      </header>

      <nav className="flex gap-1 overflow-x-auto border-b border-border">
        {TABS.map((t) => {
          const count = t.facet ? facets[t.facet] : undefined;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => selectTab(t.id)}
              className={cn(
                "shrink-0 border-b-2 px-3 py-2 text-sm font-medium transition",
                tab === t.id
                  ? "border-brand text-brand"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              {t.label}
              {count != null ? ` (${count})` : ""}
            </button>
          );
        })}
      </nav>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          className="rounded-md border border-border px-2.5 py-1.5 text-xs"
          onClick={() => setShowFilters((v) => !v)}
        >
          {showFilters ? "Hide filters" : "Show filters"}
        </button>
        <label className="flex items-center gap-1 text-xs text-muted-foreground">
          Sort
          <select
            className="rounded-md border border-border bg-background px-2 py-1 text-xs text-foreground"
            value={sort}
            onChange={(e) => setSort(e.target.value as TaskSortField)}
          >
            <option value="due">Due date</option>
            <option value="priority">Priority</option>
            <option value="company">Company</option>
            <option value="type">Type</option>
          </select>
          <select
            className="rounded-md border border-border bg-background px-2 py-1 text-xs text-foreground"
            value={sortDir}
            onChange={(e) => setSortDir(e.target.value as "asc" | "desc")}
          >
            <option value="asc">Asc</option>
            <option value="desc">Desc</option>
          </select>
        </label>
        <label className="flex items-center gap-1 text-xs text-muted-foreground">
          Group by
          <select
            className="rounded-md border border-border bg-background px-2 py-1 text-xs text-foreground"
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value as TaskGroupBy)}
          >
            <option value="none">None</option>
            <option value="due">Due date</option>
            <option value="priority">Priority</option>
            <option value="status">Status</option>
            <option value="type">Type</option>
          </select>
        </label>
        <input
          className="min-w-[180px] flex-1 rounded-md border border-border bg-background px-2 py-1.5 text-sm"
          placeholder="Search tasks…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {showFilters && (
        <div className="grid gap-2 rounded-lg border border-border p-3 sm:grid-cols-3">
          <label className="block text-xs">
            Status
            <select
              className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
              value={status}
              onChange={(e) =>
                setStatus(e.target.value as TaskStatus | "open")
              }
            >
              <option value="open">Open</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="skipped">Skipped</option>
              <option value="archived">Archived</option>
            </select>
          </label>
          <label className="block text-xs">
            Priority
            <select
              className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
              value={priority}
              onChange={(e) =>
                setPriority(e.target.value as "" | TaskPriority)
              }
            >
              <option value="">Any</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </label>
          <div className="text-xs text-muted-foreground">
            <p className="font-medium text-foreground">Summary</p>
            <p className="mt-1">
              {facets.pending} open · {facets.overdue} overdue ·{" "}
              {facets.completed} completed
            </p>
          </div>
        </div>
      )}

      {selectedIds.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-brand/30 bg-brand/5 px-3 py-2 text-sm">
          <span className="font-medium">{selectedIds.length} selected</span>
          <button
            type="button"
            className="rounded-md border border-border bg-background px-2 py-1 text-xs"
            disabled={bulkMut.isPending}
            onClick={() =>
              runBulk({ taskIds: selectedIds, action: "complete" })
            }
          >
            Complete
          </button>
          <button
            type="button"
            className="rounded-md border border-border bg-background px-2 py-1 text-xs"
            disabled={bulkMut.isPending}
            onClick={() =>
              runBulk({
                taskIds: selectedIds,
                action: "priority",
                priority: "high",
              })
            }
          >
            Priority high
          </button>
          <input
            type="date"
            className="rounded-md border border-border bg-background px-2 py-1 text-xs"
            value={bulkDue}
            onChange={(e) => setBulkDue(e.target.value)}
          />
          <button
            type="button"
            className="rounded-md border border-border bg-background px-2 py-1 text-xs"
            disabled={!bulkDue || bulkMut.isPending}
            onClick={() =>
              runBulk({
                taskIds: selectedIds,
                action: "reschedule",
                dueAt: new Date(bulkDue).toISOString(),
              })
            }
          >
            Reschedule
          </button>
          <button
            type="button"
            className="text-xs text-muted-foreground underline"
            onClick={() => setSelectedIds([])}
          >
            Clear
          </button>
        </div>
      )}

      <div
        className={cn(
          "grid gap-4",
          selected ? "lg:grid-cols-[1fr_380px]" : "",
        )}
      >
        <div className="min-w-0 overflow-x-auto overscroll-x-contain rounded-lg border border-border [-webkit-overflow-scrolling:touch]">
          {listQuery.isLoading && (
            <div className="flex justify-center py-16">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
            </div>
          )}

          {!listQuery.isLoading && tasks.length === 0 && (
            <div className="p-8 text-center text-sm text-muted-foreground">
              No tasks here.{" "}
              <button
                type="button"
                className="text-brand underline"
                onClick={() => setCreateOpen(true)}
              >
                Create a task
              </button>{" "}
              or run a{" "}
              <Link to="/sequences" className="text-brand underline">
                sequence
              </Link>
              .
            </div>
          )}

          {!listQuery.isLoading && tasks.length > 0 && (
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead className="border-b border-border bg-surface/50 text-xs text-muted-foreground">
                <tr>
                  <th className="w-8 px-2 py-2">
                    <input
                      type="checkbox"
                      checked={
                        tasks.length > 0 &&
                        selectedIds.length === tasks.length
                      }
                      onChange={toggleAll}
                    />
                  </th>
                  <th className="px-3 py-2">Task</th>
                  <th className="px-3 py-2">Contact</th>
                  <th className="px-3 py-2">Company</th>
                  <th className="px-3 py-2">Priority</th>
                  <th className="px-3 py-2">Due</th>
                  {tab === "recommended" && (
                    <th className="px-3 py-2">Why</th>
                  )}
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {grouped.map((group) => (
                  <Fragment key={group.key}>
                    {groupBy !== "none" && (
                      <tr className="bg-muted/20">
                        <td
                          colSpan={tab === "recommended" ? 8 : 7}
                          className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                        >
                          {group.key} ({group.items.length})
                        </td>
                      </tr>
                    )}
                    {group.items.map((task) => {
                      const due = dueLabel(task.dueAt);
                      return (
                        <tr
                          key={task.taskId}
                          className={cn(
                            "border-b border-border/60 hover:bg-surface-hover/40",
                            selected?.taskId === task.taskId &&
                              "bg-surface-hover/70",
                          )}
                        >
                          <td className="px-2 py-2">
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(task.taskId)}
                              onChange={() => toggleSelect(task.taskId)}
                            />
                          </td>
                          <td
                            className="cursor-pointer px-3 py-2"
                            onClick={() => setSelected(task)}
                          >
                            <div className="flex items-start gap-2">
                              <span
                                className="mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded bg-muted text-[10px] font-bold"
                                title={task.type}
                              >
                                {typeBadge(task)}
                              </span>
                              <div className="min-w-0">
                                <p className="font-medium leading-snug">
                                  {task.title}
                                </p>
                                {(task.sequenceId || task.dealName) && (
                                  <p className="truncate text-xs text-muted-foreground">
                                    {task.sequenceId
                                      ? "Sequence · "
                                      : ""}
                                    {task.dealName}
                                  </p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-muted-foreground">
                            {task.leadName || "—"}
                          </td>
                          <td className="px-3 py-2 text-muted-foreground">
                            {task.company || "—"}
                          </td>
                          <td className="px-3 py-2 capitalize">
                            {task.priority}
                          </td>
                          <td className="px-3 py-2">
                            <span
                              className={cn(
                                "text-xs font-medium",
                                due.overdue
                                  ? "text-red-500"
                                  : "text-muted-foreground",
                              )}
                            >
                              {due.text}
                            </span>
                          </td>
                          {tab === "recommended" && (
                            <td className="px-3 py-2 text-xs text-brand">
                              {task.recommendationReason || "—"}
                            </td>
                          )}
                          <td className="px-3 py-2 text-right">
                            {task.status === "pending" && (
                              <button
                                type="button"
                                className="text-xs text-brand underline disabled:opacity-40"
                                disabled={completeMut.isPending}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  completeMut.mutate(
                                    { taskId: task.taskId },
                                    {
                                      onSuccess: () => {
                                        setSelected((prev) =>
                                          prev?.taskId === task.taskId
                                            ? null
                                            : prev,
                                        );
                                        setSelectedIds((prev) =>
                                          prev.filter((id) => id !== task.taskId),
                                        );
                                      },
                                    },
                                  );
                                }}
                              >
                                Complete
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {selected && (
          <TaskWorkbench
            task={selected}
            onClose={() => setSelected(null)}
            onUpdated={(t) => {
              setSelected(t.status === "pending" ? t : null);
            }}
          />
        )}
      </div>

      <CreateTaskModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
    </div>
  );
}
